// Katalog kırılım saf yardımcıları (spec §6.4, §18 DR6/DR14) — MetricModal "Konum" seçicisi,
// CatalogTable satır türleri/eylemleri ve BreakdownConvertDialog aynı kuralları buradan okur.
// Satır türü shared `rowKindsOf` (set editörü `setBreakdownView` üzerinden — grid/rapor ile aynı
// kural); kategori kapısı shared `kpiCatalogCategoryIds` (sunucunun `isKpiCatalogCategory`'si).

import {
  KPI_BREAKDOWN_DIMENSIONS,
  KPI_BREAKDOWN_DIMENSION_LABELS,
  exactSumRef,
  kpiCatalogCategoryIds,
  normalizeUnitKey,
  slugifyKpiCode,
  stripParentPrefix,
  type KpiMetric,
  type Permissions,
} from "@mahir/shared";
import { KPI_BREAKDOWN_COPY } from "./kpi-breakdown-copy";
import { setBreakdownView, type SetBreakdownView } from "./kpi-breakdown-set";
import { buildIndicatorTree } from "./kpi-set-editor";
import { canManageCatalogCategories, SUPERADMIN_ONLY_HINT } from "./role-ui";
import { type SearchSelectOption } from "./search-select";

/** Katalog düğümü — set yardımcılarının `metricId` alanı kimliğin kendisi. */
export type CatalogNode = KpiMetric & { metricId: string };
export type CatalogView = SetBreakdownView<CatalogNode>;

/** Üye slug'ı (`kpi_metrics.dimension_member` ≤64; etiketten `slugifyKpiCode`). */
export const MEMBER_SLUG_RE = /^[a-z][a-z0-9_]{0,63}$/;
/** "Bir göstergenin alt kalemi" araması: ilk 50 sonuç (668 göstergelik katalogda ölçeklenir). */
export const TOTAL_SEARCH_LIMIT = 50;

export type LocationMode = "category" | "member" | "root";

export function catalogBreakdownView(metrics: readonly KpiMetric[]): CatalogView {
  return setBreakdownView(metrics.map((m) => ({ ...m, metricId: m.id })));
}

/** Atalar etiket zinciri ("Sosyal › Çalışan Profili"), düğümün kendisi hariç. */
export function catalogPathOf(byId: ReadonlyMap<string, KpiMetric>, id: string | null): string {
  const parts: string[] = [];
  const seen = new Set<string>();
  let cur = id ? byId.get(id) : undefined;
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    parts.unshift(cur.label);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return parts.join(" › ");
}

export function locationModeOf(d: {
  parentId: string | null;
  dimensionMember: string | null;
}): LocationMode {
  if (d.dimensionMember !== null) return "member";
  return d.parentId === null ? "root" : "category";
}

function subtreeIds(view: CatalogView, rootId: string | null | undefined): Set<string> {
  const out = new Set<string>();
  if (!rootId) return out;
  const stack = [rootId];
  while (stack.length) {
    const id = stack.pop()!;
    if (out.has(id)) continue;
    out.add(id);
    for (const c of view.childrenOf(id)) stack.push(c.id);
  }
  return out;
}

/**
 * "Bir kategori altında" seçenekleri: yalnız kategoriler (etkin tür `category` ya da grup olmayan
 * kök), kendisi ve alt ağacı hariç, etkinler; mevcut üst her durumda görünür kalır.
 */
export function categoryParentOptions(
  metrics: readonly KpiMetric[],
  view: CatalogView,
  self: KpiMetric | null,
  currentParentId: string | null,
): SearchSelectOption[] {
  const excluded = subtreeIds(view, self?.id);
  return buildIndicatorTree([...metrics])
    .rows.filter((r) => {
      const n = r.node;
      if (n.id === currentParentId) return true;
      if (excluded.has(n.id) || n.status !== "active") return false;
      const kind = view.kindOf(n.id);
      return kind === "category" || (n.parentId === null && kind !== "breakdown_group");
    })
    .map((r) => ({ value: r.node.id, label: r.node.label, code: r.node.code, depth: r.depth }));
}

/**
 * "Bir göstergenin alt kalemi" — toplam adayları: kökte olmayan, grup/üye olmayan etkin
 * göstergeler (düz gösterge ya da kırılımlı toplam), kendisi ve alt ağacı hariç. İpucu = yol.
 */
export function memberTotalOptions(
  metrics: readonly KpiMetric[],
  view: CatalogView,
  self: KpiMetric | null,
): SearchSelectOption[] {
  const byId = new Map(metrics.map((m) => [m.id, m]));
  const excluded = subtreeIds(view, self?.id);
  return metrics
    .filter((m) => {
      if (excluded.has(m.id) || m.status !== "active" || m.parentId === null) return false;
      if (m.dimensionMember) return false;
      const kind = view.kindOf(m.id);
      return kind === "metric" || kind === "total";
    })
    .map((m) => ({
      value: m.id,
      label: m.label,
      code: m.code,
      hint: catalogPathOf(byId, m.parentId),
    }))
    .sort((a, b) => `${a.hint} ${a.label}`.localeCompare(`${b.hint} ${b.label}`, "tr"));
}

/** Toplamın kırılım grupları (katalog sırası). */
export function totalGroups(view: CatalogView, totalId: string | null): readonly CatalogNode[] {
  return totalId ? view.groupsOf(totalId) : [];
}

/** Etiketten üye slug'ı (kod gövdesi kuralı; boş etiket → ""). */
export function memberSlugFor(label: string): string {
  return label.trim() === "" ? "" : slugifyKpiCode(label);
}

// ─── Kırılıma dönüştür ───────────────────────────────────────────────────────────────────────

export type ConvertPattern = "category" | "siblings";

export type SiblingIneligibleReason = "unit" | "valueType" | "derived" | "breakdown";

/**
 * Kalıp (b) aday süzgeci: birim uyumlu ∧ aynı değer tipi ∧ hesaplanan değil ∧ kendi kırılımı yok.
 * Birim kuralı sunucu `planUnit` ile aynı: boş birim (iki taraftan biri) uyumludur, yalnız iki dolu
 * ve normalize hâli farklı birim "unit". Sebep önceliği unit → valueType → derived → breakdown.
 * `hasGroups`: kardeşin altında kırılım grubu var mı (katalog görünümünden).
 */
export function partitionSiblingCandidates(
  total: KpiMetric,
  siblings: readonly KpiMetric[],
  opts: {
    hasGroups?: (id: string) => boolean;
    /** müşteri bazlı etkin türetilmiş kardeşler (önizleme `derivedMembers`) — katalog bayrağına ek */
    derivedIds?: ReadonlySet<string>;
  } = {},
): { eligible: KpiMetric[]; ineligible: { id: string; reason: SiblingIneligibleReason }[] } {
  const key = (u: string | null): string => (u ? normalizeUnitKey(u) : "");
  const unitClash = (a: string | null, b: string | null): boolean =>
    key(a) !== "" && key(b) !== "" && key(a) !== key(b);
  const eligible: KpiMetric[] = [];
  const ineligible: { id: string; reason: SiblingIneligibleReason }[] = [];
  for (const s of siblings) {
    const reason: SiblingIneligibleReason | null = unitClash(s.defaultUnit, total.defaultUnit)
      ? "unit"
      : s.valueType !== total.valueType
        ? "valueType"
        : s.isDerived || opts.derivedIds?.has(s.id)
          ? "derived"
          : opts.hasGroups?.(s.id)
            ? "breakdown"
            : null;
    if (reason) ineligible.push({ id: s.id, reason });
    else eligible.push(s);
  }
  return { eligible, ineligible };
}

/** Kalıp (b) uygunsuzluk sebebi → kısa metin (pencere satırı ve pasif menü ipucu aynı). */
export function siblingReasonText(reason: SiblingIneligibleReason, sibling?: KpiMetric): string {
  const C = KPI_BREAKDOWN_COPY.convert.ineligible;
  return reason === "unit" ? C.unit(sibling?.defaultUnit ?? null) : C[reason];
}

export interface ConvertAction {
  pattern: ConvertPattern;
  enabled: boolean;
  /** pasifken neden (düğme `title`) */
  hint: string | null;
}

/** Kalıp (a) kaynağın düz (grup olmayan) çocukları — üye olacaklar. */
export function categoryMembers(view: CatalogView, categoryId: string): CatalogNode[] {
  return view.childrenOf(categoryId).filter((c) => view.kindOf(c.id) !== "breakdown_group");
}

/**
 * Kalıp (b) kardeş havuzu: aynı üst altında, etkin, üye olmayan göstergeler. `includeTotals`:
 * kendi kırılımı olan toplamlar da gelir (sihirbaz onları pasif + sebepli gösterir); menü
 * görünürlüğü (`convertActionFor`) yalnız düz yaprakları sayar.
 */
export function siblingPool(
  view: CatalogView,
  metrics: readonly KpiMetric[],
  total: KpiMetric,
  opts: { includeTotals?: boolean } = {},
) {
  return metrics.filter((m) => {
    if (
      m.id === total.id ||
      m.parentId !== total.parentId ||
      m.status !== "active" ||
      m.dimensionMember
    ) {
      return false;
    }
    const kind = view.kindOf(m.id);
    return kind === "metric" || (opts.includeTotals === true && kind === "total");
  });
}

/**
 * Satırın "Kırılıma dönüştür" eylemi (yalnız `kpi.manage`): kategori → kalıp (a) (çocuğu yoksa
 * yok; global kategori `kpi.catalog.categories` ister — yoksa pasif "Yalnız Sistem Yöneticisi");
 * gösterge/toplam → kalıp (b) (aday kardeş yoksa yok). Grup, üye, kalkmış satır → yok.
 */
export function convertActionFor(
  metric: KpiMetric,
  metrics: readonly KpiMetric[],
  view: CatalogView,
  perms: Permissions | null | undefined,
): ConvertAction | null {
  if (perms?.["kpi.manage"] !== true || metric.status !== "active") return null;
  if (metric.dimensionMember) return null;
  const kind = view.kindOf(metric.id);
  if (kind === "category") {
    if (categoryMembers(view, metric.id).length === 0) return null;
    const gated =
      kpiCatalogCategoryIds(metrics).has(metric.id) && !canManageCatalogCategories(perms);
    return { pattern: "category", enabled: !gated, hint: gated ? SUPERADMIN_ONLY_HINT : null };
  }
  if (kind !== "metric" && kind !== "total") return null;
  if (metric.parentId === null) return null;
  const pool = siblingPool(view, metrics, metric);
  if (pool.length === 0) return null;
  // TODO-KPI-157(2): hiç uygun aday yoksa eylem sebepli pasif (pencere yalnız pasif satır açmaz)
  const part = partitionSiblingCandidates(metric, pool, {
    hasGroups: (id) => view.kindOf(id) === "total",
  });
  if (part.eligible.length === 0) {
    const first = part.ineligible[0];
    return {
      pattern: "siblings",
      enabled: false,
      hint: KPI_BREAKDOWN_COPY.convert.noEligibleSibling(
        first
          ? siblingReasonText(
              first.reason,
              pool.find((m) => m.id === first.id),
            )
          : "",
      ),
    };
  }
  return { pattern: "siblings", enabled: true, hint: null };
}

export interface ConvertTargetOption {
  metric: KpiMetric;
  /** formülü tam olarak `TOPLA(<kategori>)` — önerilen hedef */
  suggested: boolean;
}

/** Kalıp (a) hedef toplam adayları: kategorinin kardeşi olan gösterge/toplamlar (önerilen önce). */
export function convertTargetOptions(
  category: KpiMetric,
  metrics: readonly KpiMetric[],
  view: CatalogView,
): ConvertTargetOption[] {
  return metrics
    .filter(
      (m) =>
        m.id !== category.id &&
        m.parentId === category.parentId &&
        m.status === "active" &&
        !m.dimensionMember &&
        (view.kindOf(m.id) === "metric" || view.kindOf(m.id) === "total"),
    )
    .map((m) => ({
      metric: m,
      suggested: m.defaultFormulas.some((f) => exactSumRef(f.expression) === category.code),
    }))
    .sort(
      (a, b) =>
        Number(b.suggested) - Number(a.suggested) ||
        a.metric.label.localeCompare(b.metric.label, "tr"),
    );
}

const SEP_RE = /[\s\-–—:›,/|]+$/u;

/** Etiketlerin ayırıcıda biten ortak öneki ("Tam Zamanlı - Kadın", "Tam Zamanlı - Erkek" → "Tam Zamanlı"). */
export function commonLabelPrefix(labels: readonly string[]): string {
  if (labels.length < 2) return "";
  let prefix = labels[0]!;
  for (const l of labels.slice(1)) {
    let i = 0;
    while (i < prefix.length && i < l.length && prefix[i] === l[i]) i += 1;
    prefix = prefix.slice(0, i);
  }
  const cut = prefix.search(/[\s\-–—:›,/|]+[^\s\-–—:›,/|]*$/u);
  const head = cut > 0 ? prefix.slice(0, cut) : "";
  return head.replace(SEP_RE, "").trim();
}

/**
 * Üye etiketi önerisi: önce toplam etiketi öneki soyulur (shared `stripParentPrefix`); değişmezse
 * bir kardeşle paylaşılan en uzun ortak önek ("Tam Zamanlı - Kadın" → "Kadın").
 */
export function suggestMemberLabel(
  totalLabel: string,
  label: string,
  peers: readonly string[],
): string {
  const byTotal = stripParentPrefix(totalLabel, label);
  if (byTotal !== label.trim()) return byTotal;
  // en uzun ortak önek: etiketin her bir kardeşle paylaştığı (ayırıcıda biten) önek
  let prefix = "";
  for (const p of peers) {
    if (p === label) continue;
    const c = commonLabelPrefix([label, p]);
    if (c.length > prefix.length) prefix = c;
  }
  return prefix ? stripParentPrefix(prefix, label) : label.trim();
}

/**
 * Kategori etiketinden boyut önerisi ("Yaş Grubuna Göre Çalışan Sayısı" → `yas_grubu`): boyut
 * kodunun sözcükleri etiket slug'ında ardışık sözcük öneki olarak geçmeli (uzun sözcükte son harf
 * esnek — Türkçe ek). Eşleşme yoksa null.
 */
export function suggestDimension(label: string): { dimension: string; label: string } | null {
  const words = slugifyKpiCode(label).split("_");
  for (const d of KPI_BREAKDOWN_DIMENSIONS) {
    if (d === "diger") continue;
    const parts = d.split("_").map((p) => (p.length > 4 ? p.slice(0, -1) : p));
    for (let i = 0; i + parts.length <= words.length; i += 1) {
      if (parts.every((p, j) => words[i + j]!.startsWith(p))) {
        return { dimension: d, label: KPI_BREAKDOWN_DIMENSION_LABELS[d] };
      }
    }
  }
  return null;
}

/**
 * Konum kaydet kapısı (ilk eksik koşulun metni; null = geçer): üye kipinde üst bir kırılım grubu
 * ve geçerli üye kodu; kategori kipinde üst kategori; kök yalnız kategori izniyle (kök = kategori).
 */
export function locationGate(args: {
  mode: LocationMode;
  parentId: string | null;
  dimensionMember: string | null;
  view: CatalogView;
  rootAllowed: boolean;
  copy: { needGroup: string; memberSlugInvalid: string; needCategory: string; rootLocked: string };
}): string | null {
  const { mode, parentId, view, copy } = args;
  if (mode === "member") {
    if (!parentId || view.kindOf(parentId) !== "breakdown_group") return copy.needGroup;
    if (!MEMBER_SLUG_RE.test(args.dimensionMember ?? "")) return copy.memberSlugInvalid;
    return null;
  }
  if (mode === "category") return parentId ? null : copy.needCategory;
  return args.rootAllowed ? null : copy.rootLocked;
}
